import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Category as CategoryIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const FeaturesViewModal = ({
  open,
  onClose,
  category,
  isMainCategory = true,
  onDeleteSubCategory
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!category) return null;

  const handleChipDelete = (subCat, e) => {
    e.stopPropagation();
    if (onDeleteSubCategory) {
      onDeleteSubCategory(subCat.id);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 1,
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: 'white',
          py: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryIcon />
            <Typography variant="h6" fontFamily="poppinsSemiBold">
              View {isMainCategory ? 'Category' : 'Sub Category'} Details
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {isMainCategory ? 'Category Name' : 'Sub Category Name'}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {category.name || 'N/A'}
            </Typography>
          </Grid>

          {isMainCategory ? (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Sub Categories
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {category.sub_category && category.sub_category.length > 0 ? (
                  category.sub_category.map((subCat) => (
                    <Tooltip key={subCat.id} title="Click to delete">
                      <Chip
                        label={subCat.name}
                        variant="outlined"
                        size="small"
                        onDelete={(e) => handleChipDelete(subCat, e)}
                        deleteIcon={
                          <CancelIcon
                            fontSize="small"
                            sx={{
                              '&:hover': {
                                color: palette.error.main
                              }
                            }}
                          />
                        }
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: palette.action.hover,
                            '& .MuiChip-deleteIcon': {
                              color: palette.error.main
                            }
                          },
                          borderColor: palette.grey[400],
                          color: palette.text.primary,
                          '& .MuiChip-deleteIcon': {
                            transition: 'color 0.2s ease'
                          }
                        }}
                      />
                    </Tooltip>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No sub categories
                  </Typography>
                )}
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Parent Category
              </Typography>
              <Typography variant="body1">{category.parent_name || 'N/A'}</Typography>
            </Grid>
          )}

          {category.description && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1">{category.description}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontFamily: 'poppinsMedium',
            borderColor: palette.grey[400],
            color: palette.text.primary
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeaturesViewModal;
